/**
 * Team Create Route
 *
 * Create a new team using TeamForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { TeamForm } from '../../components/teams';
import { createTeam, type CreateTeamInput, ApiError } from '../../lib/apiClient';
import type { CreateTeamFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/teams/new')({
  component: TeamCreatePage,
});

function TeamCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateTeamFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateTeamInput = {
        name: formData.name,
        description: formData.description || undefined,
        type: formData.type,
        parentTeamId: formData.parentTeamId || undefined,
        leadUserId: formData.leadUserId,
        memberIds: formData.memberIds || [],
        responsibilities: formData.responsibilities || [],
        contactEmail: formData.contactEmail,
        teamsChannel: formData.teamsChannel || undefined,
      };

      const newTeam = await createTeam(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListTeams'],
      });

      // Navigate to the new team detail page
      navigate({ to: `/teams/${newTeam.entityId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/teams', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="team-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Team</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new team to the organization
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TeamForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
